import React, { useState } from 'react';
import {
  Box,
  Grid,
  Typography,
  Tooltip,
  IconButton,
  FormHelperText,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  useMediaQuery,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useDispatch, useSelector } from 'react-redux';
import ReusableModal from '../../components/MUIComponent/Utlis/Modal/Modal';
import CustomSingleSelect from '../../components/FormFields/CustomSingleSelect';
import CustomeFormInputField from '../../components/FormFields/CustomeFormInputField';
import Button from '../../components/Button/Button';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { GetCreateticket, TicketTrackingActions } from '../../api/auth/authService';
import { useSnackbar } from '../../components/MUIComponent/FeedbackComponent/SnackBar/SnackbarContext';
import CustomFileUploadButton from './CustomFileuploadButton';
import { menuUpdated } from '../../features/SidebarMenu/menuSlice';
import { useTranslation } from 'react-i18next';
import i18n from '../../i18n/i18n';
const formFields = [
  {
    name: 'title',
    label: 'Title',
    type: 'text',
    get placeholder() { return i18n.t('common.enterTitle'); },
    required: true,
  },
  {
    name: 'priority',
    label: 'Priority',
    type: 'select',
    get placeholder() { return i18n.t('support.choosePriority'); },
    required: true,
    options: [
      { value: 'low', get label() { return i18n.t('support.low'); } },
      { value: 'medium', get label() { return i18n.t('support.medium'); } },
      { value: 'high', get label() { return i18n.t('support.high'); } },
    ],
  },
  {
    name: 'type',
    label: 'Type',
    type: 'select',
    get placeholder() { return i18n.t('support.chooseType'); },
    required: true,
    options: [
      { value: 'Technical Support', get label() { return i18n.t('support.technicalSupport'); } },
      { value: 'Feedback', get label() { return i18n.t('support.feedback'); } },
      { value: 'Error / Bug Report ', get label() { return i18n.t('support.errorBugReport'); } },
      { value: 'Other ', get label() { return i18n.t('common.other'); } },
    ],
  },
  {
    name: 'description',
    label: 'Description',
    type: 'textarea',
    get placeholder() { return i18n.t('support.enterAClearDescriptionOfThe'); },
    required: true,
  },
];

const SupportModalForm = ({ open, onClose, showIcon = true, onSuccess }) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const dispatch = useDispatch();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));
  const { user } = useAuth();
  const customColors = useSelector(state => state.theme.customColors);
  const { showSnackbar } = useSnackbar();
  const [openModal, setOpenModal] = useState(false);
  const [fileName, setFileName] = useState('');
  const [formData, setFormData] = useState({});
  const [errors, setErrors] = useState({});
  const [fileUrl, setFileUrl] = useState(null);
  const isDark = theme.palette.mode === 'dark';
  const [isSubmitting, setIsSubmitting] = useState(false);

  const navigate = useNavigate();
  const MAX_FILE_SIZE = 2 * 1024 * 1024;
  const handleChange = (name, value) => {
    setFormData(prev => ({ ...prev, [name]: value }));
    setErrors(prev => ({ ...prev, [name]: '' }));
  };

  const handleFileChange = eOrFiles => {
    if (!eOrFiles) {
      setFileName('');
      setFormData(prev => ({ ...prev, attachmentFile: null }));
      setFileUrl(null);
      return;
    }

    let files = null;
    if (eOrFiles instanceof FileList) files = eOrFiles;
    else if (eOrFiles.target?.files) files = eOrFiles.target.files;

    const file = files?.[0];
    if (!file) return;

    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png'];

    if (!allowedTypes.includes(file.type)) {
      showSnackbar(t('messages.onlyJpgJpegAndPngFiles'), 'error');
      setFileName('');
      setFormData(prev => ({ ...prev, attachmentFile: null }));
      setFileUrl(null);
      return;
    }
    if (file.size > MAX_FILE_SIZE) {
      showSnackbar(t('messages.fileSizeExceedsThe2MbLimit'), 'error');
      setFileName('');
      setFormData(prev => ({ ...prev, attachmentFile: null }));
      setFileUrl(null);
      return;
    }

    setFileName(file.name);
    setFormData(prev => ({ ...prev, attachmentFile: file }));
    setFileUrl(URL.createObjectURL(file));
  };

  const validateForm = () => {
    const newErrors = {};
    formFields.forEach(field => {
      if (field.required && !formData[field.name]) {
        newErrors[field.name] = t('validation.required');
      }
    });
    return newErrors;
  };

  const handleSubmit = async () => {
    if (isSubmitting) return;

    const newErrors = validateForm();
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setIsSubmitting(true);

    try {
      const payload = new FormData();
      payload.append('Action', 'INSERT');
      payload.append('SchoolID', user?.schoolID);
      payload.append('Title', formData.title);
      payload.append('Description', formData.description);
      payload.append('Priority', formData.priority);
      payload.append('Type', formData.type);
      payload.append('ModifiedBy', user?.adminID);
      payload.append('TicketStatus', 'open');

      if (formData.attachmentFile) {
        payload.append('File', formData.attachmentFile);
      }

      const response = await TicketTrackingActions(payload);

      if (response.success) {
        showSnackbar(t('support.messages.ticketCreatedSuccessfully'), 'success');
        dispatch(menuUpdated());
        handleReset();
        if (onClose) onClose();
        else setOpenModal(false);
        if (onSuccess) onSuccess();
      }
    } catch (error) {
      showSnackbar(t('support.messages.errorCreatingTicket'), 'error');
    } finally {
      handleReset();
      setIsSubmitting(false);
    }

    navigate('/create-ticket');
  };

  const handleReset = () => {
    setFormData({});
    setFileName('');
    setErrors({});
    setFileUrl(null);
  };

  const handleModalClose = () => {
    handleReset();
    if (onClose) {
      onClose();
    } else {
      setOpenModal(false);
    }
  };

  return (
    <>
      {showIcon && user?.roleName === 'Admin' && (
        <Tooltip title={t('support.supportHelp')}>
          <IconButton
            onClick={() => setOpenModal(true)}
            sx={{
              color: theme.palette.background.text,
              borderColor: theme.palette.custom.borderColor,
              borderRadius: '15%',
            }}
          >
            <i class="fa-sharp fa-regular fa-headset fs-16"></i>
          </IconButton>
        </Tooltip>
      )}

      <ReusableModal
        open={open || openModal}
        onClose={handleModalClose}
        title={t('support.createTicket')}
        // style={{
        //   width: isMobile ? '90%' : isTablet ? '50%' : '40%',
        // }}
      >
        <form
          noValidate
          onSubmit={e => {
            e.preventDefault();
            handleSubmit();
          }}
        >
          <Box sx={{ pb: 2, pt: 2 }}>
            <Grid container rowSpacing={3} columnSpacing={4}>
              {formFields.map(field => (
                <Grid item size={{ xs: 12 }} sx={{ width: '100%' }} key={field.name}>
                  {field.type === 'text' && (
                    <CustomeFormInputField
                      name={field.name}
                      label={field.label}
                      maxLength={50}
                      placeholder={field.placeholder}
                      value={formData[field.name] || ''}
                      required={field.required}
                      error={!!errors[field.name]}
                      helperText={errors[field.name]}
                      onChange={e => handleChange(field.name, e.target.value)}
                      inputSx={{
                        background: theme.palette.background.bgwhite,
                      }}
                    />
                  )}

                  {field.type === 'select' && (
                    <CustomSingleSelect
                      name={field.name}
                      label={field.label}
                      placeholder={field.placeholder}
                      options={field.options}
                      value={formData[field.name] || ''}
                      onChange={e => handleChange(field.name, e.target.value)}
                      select
                      error={!!errors[field.name]}
                      helperText={errors[field.name]}
                      fullWidth
                      required
                    />
                  )}

                  {field.type === 'textarea' && (
                    <CustomeFormInputField
                      label={field.label}
                      placeholder={field.placeholder}
                      fullWidth
                      multiline
                      maxLength={500}
                      required={field.required}
                      rows={5}
                      value={formData[field.name] || ''}
                      onChange={e => handleChange(field.name, e.target.value)}
                      error={!!errors[field.name]}
                      helperText={errors[field.name]}
                    />
                  )}

                  {/* {errors[field.name] && <FormHelperText error>{errors[field.name]}</FormHelperText>} */}
                </Grid>
              ))}
              <Grid item xs={12} width={'100%'}>
                <Box display="flex" flexDirection="column" gap={0.5}>
                  <Typography
                    variant="subtitle2"
                    sx={{
                      fontWeight: 500,
                      color: theme.palette.custom.tableHeadingText,
                    }}
                  >
                    {t('support.fileAttachmentS')}
                  </Typography>
                  <Box
                    display="flex"
                    alignItems="center"
                    gap={2}
                    sx={{
                      background: isDark ? '#111417' : '#f8fafb',
                      padding: '7px 14px',
                      borderRadius: '8px',
                      border: `1px solid ${isDark ? ' #373a3c' : '#f4f5f5'}`,
                    }}
                  >
                    <CustomFileUploadButton
                      label={t('common.chooseFile')}
                      multiple={false}
                      icon={<i class="fa-sharp fa-regular fa-file-upload" style={{ fontSize: '20px' }} />}
                      // { <i class="fa-regular fa-upload"></i>}

                      bgColor={
                        (customColors && customColors.activeButton) ||
                        theme.palette.background.bgpurple
                      }
                      textColor={customColors?.activeTextColor || theme.palette.custom.whitetext}
                      onChange={handleFileChange}
                      accept=".jpg,.jpeg,.png"
                    />
                    <Typography
                      variant="body2"
                      component={fileName ? 'a' : 'span'}
                      href={fileName ? fileUrl : undefined}
                      download={fileName || undefined}
                      sx={{
                        color: fileName ? 'primary.main' : 'text.secondary',

                        textDecoration: fileName ? 'none' : 'none',
                        '&:hover': fileName
                          ? {
                              textDecoration: 'underline',
                            }
                          : {},
                      }}
                    >
                      {fileName || t('common.noFileChosen')}
                    </Typography>
                  </Box>
                </Box>
              </Grid>

              <Grid item size={{ xs: 12 }} sx={{ width: '100%' }}>
                <Box
                  sx={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    justifyContent: 'flex-end',
                    gap: 1,
                  }}
                >
                  <Button
                    type="button"
                    sx={{
                      minWidth: 90,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '2px',
                    }}
                    bgColor="#e7e7e8"
                    textColor="#1f2937"
                    onClick={handleReset}
                  >
                    <i
                      class="fa-sharp fa-regular fa-arrows-rotate"
                      style={{ fontSize: '11px' }}
                    ></i>{' '}
                    {t('common.reset')}
                  </Button>
                  <Button
                    type="submit"
                    isDisabled={isSubmitting}
                    sx={{
                      minWidth: 90,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '2px',
                    }}
                    bgColor={
                      (customColors && customColors.activeButton) ||
                      theme.palette.background.bgpurple
                    }
                    textColor={customColors?.activeTextColor || theme.palette.custom.whitetext}
                  >
                    <i class="fa-sharp fa-regular fa-check" style={{ fontSize: '12px' }}></i>{' '}
                    {isSubmitting ? t('common.submitting') : t('common.submit')}
                  </Button>
                </Box>
              </Grid>
            </Grid>
            {/* {submittedData.length > 0 && (
            <Box mt={4}>
              <Typography variant="h6" mb={1}>
                Submitted Tickets
              </Typography>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Title</TableCell>
                    <TableCell>Priority</TableCell>
                    <TableCell>Type</TableCell>
                    <TableCell>Description</TableCell>
                    <TableCell>File</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {submittedData.map((row, index) => (
                    <TableRow key={index}>
                      <TableCell>{row.title}</TableCell>
                      <TableCell>{row.priority}</TableCell>
                      <TableCell>{row.type}</TableCell>
                      <TableCell>{row.description}</TableCell>
                      <TableCell>{row.file}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          )} */}
          </Box>
        </form>
      </ReusableModal>
    </>
  );
};

export default SupportModalForm;
